import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CopyButton } from "../components/ui/CopyButton";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { OperatorSurface } from "../components/ui/OperatorSurface";
import { NonWorkspacePage, PageEmptyState, PageErrorState, PageLoadingState } from "../components/layout/NonWorkspacePage";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useAuth } from "../hooks/useAuth";
import { type BrandDeleteResponse, useBrands, useDeleteBrand } from "../hooks/useBrand";
import { ApiError } from "../lib/api";
import type { Brand } from "../../../shared/types";

type DeleteBrand = (brandId: string, confirm?: boolean) => Promise<BrandDeleteResponse>;

function deleteErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return "This brand no longer exists or you no longer have access to delete it.";
  }
  return error instanceof Error ? error.message : "We couldn't delete this brand. Please try again.";
}

export function Brands() {
  const { user } = useAuth();
  const { activeBrandId } = useActiveBrand();
  const brandsQuery = useBrands();
  const deleteBrand = useDeleteBrand();
  return <BrandsView brands={brandsQuery.data?.data ?? []} currentUserId={user?.id} currentUserEmail={user?.email} activeBrandId={activeBrandId} isLoading={brandsQuery.isLoading} isError={brandsQuery.isError} onRetry={() => void brandsQuery.refetch()} deleteBrand={(brandId, confirm) => deleteBrand.mutateAsync({ brandId, confirm })} />;
}

export function BrandsView({ brands, currentUserId, currentUserEmail, activeBrandId, isLoading, isError, onRetry, deleteBrand }: {
  brands: Brand[];
  currentUserId?: string;
  currentUserEmail?: string;
  activeBrandId?: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  deleteBrand: DeleteBrand;
}) {
  const navigate = useNavigate();
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [preview, setPreview] = useState<{ pg: number; nosql: number } | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const previewRequest = useRef(0);

  const closeDelete = () => {
    if (isDeleting) return;
    previewRequest.current += 1;
    setSelectedBrand(null);
    setPreview(null);
    setConfirmation("");
    setDeleteError(null);
  };

  const openDelete = async (brand: Brand) => {
    const requestId = ++previewRequest.current;
    setSelectedBrand(brand);
    setPreview(null);
    setConfirmation("");
    setDeleteError(null);
    setIsPreviewing(true);
    try {
      const response = await deleteBrand(brand.id);
      if (previewRequest.current !== requestId) return;
      if (!("dry_run" in response.data)) throw new Error("The deletion preview returned an unexpected response.");
      setPreview({
        pg: Object.values(response.data.would_delete.pg).reduce((sum, count) => sum + count, 0),
        nosql: Object.values(response.data.would_delete.nosql).reduce((sum, count) => sum + count, 0),
      });
    } catch (error) {
      if (previewRequest.current === requestId) setDeleteError(deleteErrorMessage(error));
    } finally {
      if (previewRequest.current === requestId) setIsPreviewing(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedBrand || confirmation !== selectedBrand.name) return;
    setDeleteError(null);
    setIsDeleting(true);
    try {
      const response = await deleteBrand(selectedBrand.id, true);
      if ("dry_run" in response.data) {
        setDeleteError("Brand deletion is currently unavailable. No data was deleted.");
        return;
      }
      setSelectedBrand(null);
      setPreview(null);
      setConfirmation("");
    } catch (error) {
      setDeleteError(deleteErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <NonWorkspacePage width="narrow" header={{
      eyebrow: "Account",
      title: "Brands",
      description: "Choose a brand to manage its identity, context, and work.",
      breadcrumbs: [{ label: "Workspace", href: "/home" }, { label: "Brands" }],
    }} primaryAction={{ label: "Create brand", onClick: () => navigate("/brands/new") }}>
      {(currentUserEmail || currentUserId) && <details className="mb-4 rounded-xl border border-slate-200 p-3 text-sm text-slate-600">
        <summary className="cursor-pointer font-medium">Signed-in account{currentUserEmail ? <>: <span>{currentUserEmail}</span></> : null}</summary>
        {currentUserId && <div className="mt-2 space-y-2"><p>Account ID: <code className="break-all">{currentUserId}</code></p><CopyButton text={currentUserId} label="Copy account ID" /></div>}
      </details>}
      <section aria-labelledby="your-brands-heading" className="space-y-3">
        <h2 id="your-brands-heading" className="font-data text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Your brands</h2>
        {isLoading ? (
          <PageLoadingState>Loading your brands…</PageLoadingState>
        ) : isError ? (
          <PageErrorState title="We couldn't load your brands." description="Your existing brands have not been changed." action={<Button variant="secondary" onClick={onRetry}>Retry</Button>} />
        ) : brands.length === 0 ? (
          <PageEmptyState title="Create your first brand" description="Add the identity the agent should use for your work." action={<Link className="inline-flex rounded-xl bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700" to="/brands/new">Create brand</Link>} />
        ) : (
          <div className="space-y-3">
            {brands.map((brand) => (
              <OperatorSurface key={brand.id} density="compact" className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-start gap-2">
                    <h2 className="text-base font-semibold leading-6 text-slate-950">{brand.name}</h2>
                    {brand.id === activeBrandId ? <Badge variant="info">Active</Badge> : null}
                  </div>
                  <details className="mt-2 text-sm text-slate-600">
                    <summary className="cursor-pointer">Brand identity</summary>
                    <p className="my-2">Brand ID: <code className="break-all">{brand.id}</code></p>
                    <CopyButton text={brand.id} label="Copy brand ID" />
                  </details>
                  <p className="mt-1 text-sm leading-6 text-slate-600">{[brand.industry, brand.target_audience].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <Link className="text-sm font-medium text-brand-700 hover:text-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2" to={`/brand/${encodeURIComponent(brand.id)}/settings/profile`}>Manage brand</Link>
                  {brand.user_id === currentUserId ? <Button size="sm" variant="ghost" aria-label={`Delete ${brand.name}`} onClick={() => void openDelete(brand)}>Delete</Button> : null}
                </div>
              </OperatorSurface>
            ))}
          </div>
        )}
      </section>
      <Modal open={selectedBrand !== null} onClose={closeDelete} title={selectedBrand ? `Delete ${selectedBrand.name}?` : "Delete brand?"}>
        <div className="space-y-4">
          <p className="text-sm leading-6 text-slate-600">This permanently deletes the brand and its saved work. This action cannot be undone.</p>
          {isPreviewing ? <p role="status" className="text-sm text-slate-600">Checking what will be deleted…</p> : null}
          {preview ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              <p className="font-medium text-slate-900">Deletion preview</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>{preview.pg.toLocaleString()} database {preview.pg === 1 ? "record" : "records"}</li>
                <li>{preview.nosql.toLocaleString()} stored {preview.nosql === 1 ? "document" : "documents"}</li>
              </ul>
            </div>
          ) : null}
          {deleteError ? <p role="alert" className="text-sm text-error">{deleteError}</p> : null}
          {!isPreviewing && !preview ? <Button variant="secondary" onClick={() => selectedBrand && void openDelete(selectedBrand)}>Retry preview</Button> : null}
          {preview && selectedBrand ? (
            <Input
              label={`Type ${selectedBrand.name} to confirm`}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="off"
              disabled={isDeleting}
            />
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={closeDelete} disabled={isDeleting}>Cancel</Button>
            <Button variant="danger" onClick={() => void confirmDelete()} disabled={!selectedBrand || !preview || confirmation !== selectedBrand.name} loading={isDeleting}>Delete brand</Button>
          </div>
        </div>
      </Modal>
    </NonWorkspacePage>
  );
}
